import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { ErrorNotice } from '@/components/ErrorNotice'
import { TextField } from '@/components/Form'
import { Skeleton } from '@/components/Skeleton'
import { useAccount, useUpdateEmail } from '@/hooks/useAccount'
import { useAuth } from '@/hooks/useAuth'
import { formatDate } from '@/lib/format'
import { validateEmail } from '@/lib/validation'

export function EmailCard() {
  const { user } = useAuth()
  const account = useAccount()
  const updateEmail = useUpdateEmail()
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)

  const serverEmail = account.data?.user.email ?? user?.email ?? ''

  useEffect(() => {
    if (!touched) setEmail(serverEmail)
  }, [serverEmail, touched])

  const error = touched ? validateEmail(email) : null
  const dirty = email.trim() !== (serverEmail ?? '').trim()
  const expiresAt = account.data?.expiresAt ?? account.data?.user.expiresAt ?? user?.expiresAt ?? null

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (error) return
    updateEmail.mutate(email, { onSuccess: () => setTouched(false) })
  }

  return (
    <Card>
      <CardHeader title="Your account" description="Signed in with your media server login." />
      <CardBody className="space-y-4">
        {/* Column labels above values, board-style: the same stacked shape the
            design system uses instead of a table that would scroll on a phone. */}
        <dl className="grid gap-3 sm:grid-cols-3">
          <div>
            <dt className="signage text-[9px] text-faint">Username</dt>
            <dd className="mt-1 font-mono text-sm text-ink">{user?.username ?? '—'}</dd>
          </div>
          <div>
            <dt className="signage text-[9px] text-faint">Access profile</dt>
            <dd className="mt-1 text-sm text-ink">
              {account.isPending ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                (account.data?.profile?.name ?? 'Standard')
              )}
            </dd>
          </div>
          <div>
            <dt className="signage text-[9px] text-faint">Access expires</dt>
            <dd className="mt-1 font-mono text-sm text-ink">
              {expiresAt ? formatDate(expiresAt) : 'Never'}
            </dd>
          </div>
        </dl>

        {account.isError ? <ErrorNotice error={account.error} compact /> : null}

        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              error={error}
              hint="Only used for account notices. Leave blank to remove it."
              onChange={(event) => {
                setTouched(true)
                setEmail(event.target.value)
              }}
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            loading={updateEmail.isPending}
            disabled={!dirty || Boolean(error)}
          >
            Save
          </Button>
        </form>
      </CardBody>
    </Card>
  )
}
